import { useQuery } from '@apollo/client';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from 'contexts/user/UserContext';
import IntlMessages from 'helpers/IntlMessages';
import { USER_PROFILE } from 'services/user';
import { requestForumChat } from 'services/forumChat';
import { fetchDiscourseUserChatability } from 'services/discourseChatability';
import ChatRedirect from './ChatRedirect';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';

type ChatabilityState = 'loading' | 'chatable' | 'inactive' | 'error';
type RequestState = 'idle' | 'sending' | 'sent' | 'already-sent' | 'failed';

const ChatRouter: React.FC = () => {
  const { currentUser } = useContext(UserContext);
  const history = useHistory();
  const { username } = useParams<{ username?: string }>();
  const requestAttemptedRef = useRef(false);
  const discourseUrl = import.meta.env.VITE_DISCOURSE_URL as string || '';
  const intl = useIntl();

  const { data, loading } = useQuery(USER_PROFILE, {
    variables: { username },
    skip: !username,
  });

  const targetUser = data?.userByUsername;
  const [chatabilityState, setChatabilityState] = useState<ChatabilityState>('loading');
  const [requestState, setRequestState] = useState<RequestState>('idle');

  useEffect(() => {
    requestAttemptedRef.current = false;
    setChatabilityState('loading');
    setRequestState('idle');
  }, [username]);

  useEffect(() => {
    if (loading || !username || !targetUser || !currentUser?.username) {
      return;
    }

    if (currentUser.username === targetUser.username) {
      setChatabilityState('chatable');
      return;
    }

    let cancelled = false;

    setChatabilityState('loading');
    fetchDiscourseUserChatability(targetUser.username, true)
      .then((canChat) => {
        if (cancelled) {
          return;
        }
        setChatabilityState(canChat ? 'chatable' : 'inactive');
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.warn('Failed to fetch fresh ARK forum chatability.', error);
        setChatabilityState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.username, loading, targetUser, targetUser?.username, username]);

  useEffect(() => {
    if (chatabilityState !== 'inactive' || requestAttemptedRef.current || !targetUser || !currentUser?.username) {
      return;
    }

    if (currentUser.username === targetUser.username) {
      return;
    }

    requestAttemptedRef.current = true;
    let cancelled = false;
    setRequestState('sending');

    requestForumChat(targetUser.username)
      .then((result) => {
        if (cancelled) {
          return;
        }

        if (result.chatable) {
          setChatabilityState('chatable');
          return;
        }

        if (result.alreadyRequested) {
          setRequestState('already-sent');
          return;
        }

        if (result.queued || result.ok) {
          setRequestState('sent');
          return;
        }

        setRequestState('sent');
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        console.warn('Failed to queue ARK forum chat request email.', error);
        setRequestState('failed');
      });

    return () => {
      cancelled = true;
    };
  }, [chatabilityState, currentUser?.username, targetUser]);

  if (currentUser === null || currentUser === undefined) {
    return null;
  }

  if (!discourseUrl) {
    return null;
  }

  if (!username) {
    return <ChatRedirect />;
  }

  if (loading || !targetUser) {
    if (!loading && !targetUser) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', padding: '2rem' }}>
          <div className="text-center" style={{ maxWidth: '42rem' }}>
            <h3 className="mb-3">{intl.formatMessage({
              id: 'chat.request.user-not-found',
            })}</h3>
          </div>
        </div>
      );
    }

    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border" role="status" />
          <p className="mt-3"><IntlMessages id="general.loading" /></p>
        </div>
      </div>
    );
  }

  if (currentUser.username === targetUser.username) {
    return <ChatRedirect />;
  }

  if (chatabilityState === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border" role="status" />
          <p className="mt-3"><IntlMessages id="general.loading" /></p>
        </div>
      </div>
    );
  }

  if (chatabilityState === 'chatable') {
    return <ChatRedirect />;
  }

  return (
    <div className="d-flex justify-content-center align-items-start" style={{ minHeight: '100vh', padding: '3rem 2rem 2rem' }}>
      <div className="text-center" style={{ maxWidth: '42rem' }}>
        <img
          src="/assets/img/stewards.png"
          alt=""
          aria-hidden="true"
          style={{ width: '100%', maxWidth: '18rem', height: 'auto', marginBottom: '1.5rem' }}
        />
        <h3 className="mb-3">{intl.formatMessage({ id: 'chat.request.inactive-message' })}</h3>
        {requestState === 'sending' && (
          <p className="mb-3">
            <IntlMessages id="chat.request.inactive-message.sending" />
          </p>
        )}
        {(requestState === 'sent' || requestState === 'already-sent') && (
          <p className="mb-3">
            <IntlMessages id="chat.request.inactive-message.request-sent" />
          </p>
        )}
        {requestState === 'failed' && (
          <p className="mb-3 text-danger">
            <IntlMessages id="chat.request.inactive-message.request-failed" />
          </p>
        )}
        {chatabilityState === 'error' && (
          <p className="mb-3 text-danger">
            <IntlMessages id="chat.request.inactive-message.chatability-error" />
          </p>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => history.push(`/user/${targetUser.username}`)}
        >
          <IntlMessages id="redirect.back" defaultMessage="Go back" />
        </button>
      </div>
    </div>
  );
};

export default ChatRouter;
